<script>
import { supabase } from "../lib/supabase"
import {
  buildCategoryPathMap,
  flattenCategoriesWithDepth,
  normalizeParentId,
  sortRows,
} from "../lib/categoryHierarchy"

export default {
  data() {
    return {
      loading: false,
      error: null,
      success: null,
      session: null,
      authEmail: "",
      authPassword: "",
      uploading: false,
      categories: [],
      subcategories: [],
      items: [],
      newCategory: {
        title: "",
        cover: "",
        sort: 0,
        parent_id: "",
      },
      newSub: {
        category_id: "",
        title: "",
        sort: 0,
      },
      newItem: {
        subcategory_id: "",
        name: "",
        description: "",
        price: "",
        spice: "",
        image: "",
        sort: "",
      },
    }
  },
  mounted() {
    this.initAuth()
  },
  computed: {
    isAuthed() {
      return !!this.session
    },
    orderedCategories() {
      return flattenCategoriesWithDepth(this.categories)
    },
    rootCategoryOptions() {
      return sortRows(this.categories.filter(category => !normalizeParentId(category.parent_id)))
    },
    categoryNames() {
      return buildCategoryPathMap(this.categories)
    },
    filteredSubs() {
      if (!this.newSub.category_id) return this.subcategories
      return this.subcategories.filter(s => s.category_id === this.newSub.category_id)
    },
    subcategoryNames() {
      return this.subcategories.reduce((acc, sub) => {
        const categoryLabel = this.categoryNames[sub.category_id] || "-"
        acc[sub.id] = `${categoryLabel} / ${sub.title}`
        return acc
      }, {})
    },
    itemSubs() {
      return sortRows(this.subcategories)
    },
    orderedSubcategories() {
      return sortRows(this.subcategories)
    },
    orderedItems() {
      return sortRows(this.items)
    },
  },
  methods: {
    async initAuth() {
      const { data } = await supabase.auth.getSession()
      this.session = data.session

      supabase.auth.onAuthStateChange((_event, session) => {
        this.session = session
        if (this.session) {
          this.fetchAdminData()
        }
      })

      if (this.session) {
        this.fetchAdminData()
      }
    },
    async signIn() {
      this.error = null
      this.success = null

      if (!this.authEmail || !this.authPassword) {
        this.error = "E-posta ve sifre gerekli."
        return
      }

      const { error } = await supabase.auth.signInWithPassword({
        email: this.authEmail,
        password: this.authPassword,
      })

      if (error) {
        this.error = "Giris basarisiz."
      }
    },
    async signOut() {
      await supabase.auth.signOut()
      this.session = null
    },
    async fetchAdminData() {
      this.loading = true
      this.error = null
      this.success = null

      const [
        { data: categories, error: catErr },
        { data: subcategories, error: subErr },
        { data: items, error: itemErr },
      ] = await Promise.all([
        supabase
          .from("categories")
          .select("id,title,cover,sort,parent_id"),
        supabase
          .from("subcategories")
          .select("id,title,category_id,sort")
          .order("sort", { ascending: true }),
        supabase
          .from("items")
          .select("id,name,subcategory_id,image,sort,spice")
          .order("sort", { ascending: true }),
      ])

      if (catErr || subErr || itemErr) {
        this.error = "Admin verileri yuklenemedi."
      } else {
        this.categories = categories || []
        this.subcategories = subcategories || []
        this.items = items || []
      }

      this.loading = false
    },
    normalizeInsertSort(scopeRows, requestedSort) {
      const currentMax = (scopeRows || [])
        .map(row => Number(row.sort) || 0)
      const maxSort = currentMax.length ? Math.max(...currentMax) : 0
      const parsed = Number.parseInt(requestedSort, 10)

      if (!Number.isFinite(parsed) || parsed < 1) {
        return maxSort + 1
      }

      if (parsed > maxSort + 1) {
        return maxSort + 1
      }

      return parsed
    },
    async shiftSortsForInsert(tableName, scopeRows, targetSort) {
      const shifted = sortRows(scopeRows || [])
        .filter(row => (Number(row.sort) || 0) >= targetSort)

      for (let i = shifted.length - 1; i >= 0; i--) {
        const row = shifted[i]
        const nextSort = (Number(row.sort) || 0) + 1
        await supabase
          .from(tableName)
          .update({ sort: nextSort })
          .eq("id", row.id)
      }
    },
    async reindexScope(tableName, scopeRows) {
      const ordered = scopeRows || []
      const updates = ordered.map((row, index) => {
        const nextSort = index + 1
        if ((Number(row.sort) || 0) === nextSort) return null
        return supabase
          .from(tableName)
          .update({ sort: nextSort })
          .eq("id", row.id)
      }).filter(Boolean)

      if (!updates.length) return
      await Promise.all(updates)
    },
    async moveItemInScope(tableName, scopeRows, itemId, delta) {
      const ordered = sortRows(scopeRows || [])
      const index = ordered.findIndex(item => item.id === itemId)
      if (index === -1) return

      const target = index + delta
      if (target < 0 || target >= ordered.length) return

      const reordered = [...ordered]
      ;[reordered[index], reordered[target]] = [reordered[target], reordered[index]]

      await this.reindexScope(tableName, reordered)
      await this.fetchAdminData()
    },
    canMoveInScope(scopeRows, itemId, delta) {
      const ordered = sortRows(scopeRows || [])
      const index = ordered.findIndex(item => item.id === itemId)
      if (index === -1) return false
      const target = index + delta
      return target >= 0 && target < ordered.length
    },
    async moveCategory(cat, delta) {
      const scope = this.categories.filter(item => normalizeParentId(item.parent_id) === normalizeParentId(cat.parent_id))
      await this.moveItemInScope("categories", scope, cat.id, delta)
    },
    async moveSubcategory(sub, delta) {
      const scope = this.subcategories.filter(item => item.category_id === sub.category_id)
      await this.moveItemInScope("subcategories", scope, sub.id, delta)
    },
    async moveItem(item, delta) {
      const scope = this.items.filter(itemRow => itemRow.subcategory_id === item.subcategory_id)
      await this.moveItemInScope("items", scope, item.id, delta)
    },
    async createCategory() {
      this.error = null
      this.success = null

      if (!this.newCategory.title || !this.newCategory.cover) {
        this.error = "Kategori adi ve gorsel URL zorunlu."
        return
      }

      const parentId = normalizeParentId(this.newCategory.parent_id)
      const scope = this.categories.filter(item => normalizeParentId(item.parent_id) === parentId)
      const nextSort = this.normalizeInsertSort(scope, this.newCategory.sort)
      await this.shiftSortsForInsert("categories", scope, nextSort)

      const { error } = await supabase.from("categories").insert({
        title: this.newCategory.title,
        cover: this.newCategory.cover,
        sort: nextSort,
        parent_id: parentId,
      })

      if (error) {
        this.error = "Kategori eklenemedi."
        return
      }

      this.success = "Kategori eklendi."
      this.newCategory = { title: "", cover: "", sort: 0, parent_id: "" }
      this.fetchAdminData()
    },
    async uploadCategoryCover(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      this.uploading = true
      this.error = null
      this.success = null

      const path = `covers/${Date.now()}-${file.name}`
      const { error } = await supabase
        .storage
        .from("menu-images")
        .upload(path, file, { cacheControl: "3600", upsert: false })

      if (error) {
        this.error = "Gorsel yuklenemedi."
        this.uploading = false
        return
      }

      const { data } = supabase.storage.from("menu-images").getPublicUrl(path)
      this.newCategory.cover = data.publicUrl
      this.success = "Gorsel yuklendi."
      this.uploading = false
    },
    async createSubcategory() {
      this.error = null
      this.success = null

      if (!this.newSub.title || !this.newSub.category_id) {
        this.error = "Sub kategori adi ve ana kategori secimi zorunlu."
        return
      }

      const scope = this.subcategories.filter(item => item.category_id === this.newSub.category_id)
      const nextSort = this.normalizeInsertSort(scope, this.newSub.sort)
      await this.shiftSortsForInsert("subcategories", scope, nextSort)

      const { error } = await supabase.from("subcategories").insert({
        title: this.newSub.title,
        category_id: this.newSub.category_id,
        sort: nextSort,
      })

      if (error) {
        this.error = "Sub kategori eklenemedi."
        return
      }

      this.success = "Sub kategori eklendi."
      this.newSub = { category_id: "", title: "", sort: 0 }
      this.fetchAdminData()
    },
    async createItem() {
      this.error = null
      this.success = null

      if (!this.newItem.name || !this.newItem.subcategory_id) {
        this.error = "Urun adi ve sub kategori secimi zorunlu."
        return
      }

      const sanitizedSpice = Math.min(
        5,
        Math.max(0, Number.parseInt(this.newItem.spice, 10) || 0)
      )
      const requestedSort = Number.parseInt(this.newItem.sort, 10)
      const scope = this.items.filter(item => item.subcategory_id === this.newItem.subcategory_id)
      const nextSort = this.normalizeInsertSort(scope, Number.isFinite(requestedSort) ? requestedSort : 0)
      await this.shiftSortsForInsert("items", scope, nextSort)

      const { error } = await supabase.from("items").insert({
        name: this.newItem.name,
        description: this.newItem.description,
        price: Number(this.newItem.price) || 0,
        spice: sanitizedSpice,
        image: this.newItem.image,
        subcategory_id: this.newItem.subcategory_id,
        sort: nextSort,
      })

      if (error) {
        this.error = "Urun eklenemedi."
        return
      }

      this.success = "Urun eklendi."
      this.newItem = {
        subcategory_id: "",
        name: "",
        description: "",
        price: "",
        spice: "",
        image: "",
        sort: "",
      }
      this.fetchAdminData()
    },
    async uploadItemImage(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      this.uploading = true
      this.error = null
      this.success = null

      const path = `items/${Date.now()}-${file.name}`
      const { error } = await supabase
        .storage
        .from("menu-images")
        .upload(path, file, { cacheControl: "3600", upsert: false })

      if (error) {
        this.error = "Gorsel yuklenemedi."
        this.uploading = false
        return
      }

      const { data } = supabase.storage.from("menu-images").getPublicUrl(path)
      this.newItem.image = data.publicUrl
      this.success = "Gorsel yuklendi."
      this.uploading = false
    },
  },
}
</script>

<template>
  <div class="admin">
    <header class="header">
      <button class="back" @click="$router.push('/')">Menüye geri dön</button>
      <h1>Admin Panel</h1>
    </header>

    <div v-if="loading" class="state">Yukleniyor...</div>
    <div v-else-if="error" class="state">{{ error }}</div>
    <div v-else-if="success" class="state success">{{ success }}</div>

    <section v-if="!isAuthed" class="section">
      <h2>Giris</h2>
      <div class="card">
        <input v-model="authEmail" placeholder="E-posta" />
        <input v-model="authPassword" type="password" placeholder="Sifre" />
        <button @click="signIn">Giris Yap</button>
      </div>
    </section>

    <div v-else class="authed">
      <div class="section">
        <button class="ghost" @click="signOut">Cikis</button>
      </div>

      <section class="section">
        <h2>Kategori Sıralama</h2>
        <div class="card sort-list">
          <div
            v-for="cat in orderedCategories"
            :key="cat.id"
            class="sort-row"
          >
            <span class="sort-label" :style="{ paddingLeft: `${cat.depth * 18}px` }">
              {{ cat.depth ? "Alt sayfa" : "Ana kategori" }}: {{ cat.title }}
            </span>
            <div class="sort-actions">
              <button
                class="up"
                @click="moveCategory(cat, -1)"
                :disabled="!canMoveInScope(categories.filter(item => (item.parent_id || '') === (cat.parent_id || '')), cat.id, -1)"
              >
                ↑
              </button>
              <button
                class="down"
                @click="moveCategory(cat, 1)"
                :disabled="!canMoveInScope(categories.filter(item => (item.parent_id || '') === (cat.parent_id || '')), cat.id, 1)"
              >
                ↓
              </button>
            </div>
          </div>
          <div v-if="!orderedCategories.length" class="state">Kategori bulunamadı.</div>
        </div>
      </section>

      <section class="section">
        <h2>Sub Kategori Sıralama</h2>
        <div class="card sort-list">
          <div
            v-for="(sub, idx) in orderedSubcategories"
            :key="`sub-${sub.id}`"
            class="sort-row"
          >
            <span>{{ idx + 1 }}. {{ sub.title }} ({{ categoryNames[sub.category_id] || "-" }})</span>
            <div class="sort-actions">
              <button
                class="up"
                @click="moveSubcategory(sub, -1)"
                :disabled="!canMoveInScope(subcategories.filter(item => item.category_id === sub.category_id), sub.id, -1)"
              >
                ↑
              </button>
              <button
                class="down"
                @click="moveSubcategory(sub, 1)"
                :disabled="!canMoveInScope(subcategories.filter(item => item.category_id === sub.category_id), sub.id, 1)"
              >
                ↓
              </button>
            </div>
          </div>
          <div v-if="!orderedSubcategories.length" class="state">Sub kategori bulunamadı.</div>
        </div>
      </section>

      <section class="section">
        <h2>Ürün Sıralama</h2>
        <div class="card sort-list">
          <div
            v-for="(item, idx) in orderedItems"
            :key="`item-${item.id}`"
            class="sort-row"
          >
            <span>{{ idx + 1 }}. {{ item.name }} ({{ subcategoryNames[item.subcategory_id] || "-" }})</span>
            <div class="sort-actions">
              <button
                class="up"
                @click="moveItem(item, -1)"
                :disabled="!canMoveInScope(items.filter(row => row.subcategory_id === item.subcategory_id), item.id, -1)"
              >
                ↑
              </button>
              <button
                class="down"
                @click="moveItem(item, 1)"
                :disabled="!canMoveInScope(items.filter(row => row.subcategory_id === item.subcategory_id), item.id, 1)"
              >
                ↓
              </button>
            </div>
          </div>
          <div v-if="!orderedItems.length" class="state">Ürün bulunamadı.</div>
        </div>
      </section>

      <section class="section">
        <h2>Ana Kategori Ekle</h2>
        <div class="card">
          <select v-model="newCategory.parent_id">
            <option value="">Bagimsiz ana kategori</option>
            <option v-for="c in rootCategoryOptions" :key="`parent-${c.id}`" :value="c.id">
              {{ c.title }}
            </option>
          </select>
          <input v-model="newCategory.title" placeholder="Kategori adi" />
          <input v-model="newCategory.cover" placeholder="Gorsel URL" />
          <input type="file" @change="uploadCategoryCover" />
          <input v-model="newCategory.sort" type="number" placeholder="Sira" />
          <button @click="createCategory" :disabled="uploading">
            {{ uploading ? "Yukleniyor..." : "Kaydet" }}
          </button>
        </div>
      </section>

      <section class="section">
        <h2>Yeni Sub Tab</h2>
        <div class="card">
          <select v-model="newSub.category_id">
            <option value="">Ana kategori sec</option>
            <option v-for="c in categories" :key="c.id" :value="c.id">
              {{ categoryNames[c.id] || c.title }}
            </option>
          </select>
          <input v-model="newSub.title" placeholder="Sub kategori adi" />
          <input v-model="newSub.sort" type="number" placeholder="Sira" />
          <button @click="createSubcategory">Kaydet</button>
        </div>
      </section>

      <section class="section">
        <h2>Yeni Urun</h2>
        <div class="card">
          <select v-model="newItem.subcategory_id">
            <option value="">Sub kategori sec</option>
            <option v-for="s in itemSubs" :key="s.id" :value="s.id">
              {{ subcategoryNames[s.id] || s.title }}
            </option>
          </select>
          <input v-model="newItem.name" placeholder="Urun adi" />
          <input v-model="newItem.description" placeholder="Icindekiler" />
          <input v-model="newItem.price" type="number" placeholder="Fiyat" />
          <input
            v-model="newItem.spice"
            type="number"
            placeholder="Acı oranı (0-5) — varsayılan: 0"
            min="0"
            max="5"
            step="1"
          />
          <input v-model="newItem.image" placeholder="Gorsel URL" />
          <input type="file" @change="uploadItemImage" />
          <input
            v-model="newItem.sort"
            type="number"
            placeholder="Sıralama (1,2,3...) — varsayılan: 0"
          />
          <button @click="createItem" :disabled="uploading">
            {{ uploading ? "Yukleniyor..." : "Kaydet" }}
          </button>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
.admin {
  padding-bottom: 40px;
}
.header {
  padding: 16px;
  color: var(--text);
  background: linear-gradient(180deg, rgba(8, 26, 51, 0.95), rgba(8, 26, 51, 0));
}
.back {
  background: transparent;
  color: var(--muted);
  border: 1px solid rgba(73, 182, 255, 0.25);
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  cursor: pointer;
}
.section {
  padding: 10px 16px 0;
}
.section h2 {
  color: var(--text);
  font-size: 16px;
  margin: 10px 0 8px;
}
.card {
  background: var(--card-strong);
  border-radius: 18px;
  padding: 14px;
  border: 1px solid rgba(73, 182, 255, 0.12);
  box-shadow: 0 16px 30px rgba(3, 12, 30, 0.45);
  display: grid;
  gap: 10px;
}
.sort-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.sort-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(4, 20, 42, 0.5);
  border: 1px solid rgba(73, 182, 255, 0.15);
  padding: 8px 10px;
  border-radius: 10px;
  color: var(--text);
}
.sort-label {
  flex: 1;
}
.sort-actions {
  display: inline-flex;
  gap: 6px;
}
.sort-actions button {
  width: 30px;
  height: 30px;
  border-radius: 999px;
  border: 1px solid rgba(73, 182, 255, 0.25);
  background: rgba(8, 18, 33, 0.7);
  color: var(--text);
  cursor: pointer;
}
.sort-actions button:disabled {
  opacity: 0.4;
  cursor: default;
}
.card input,
.card select {
  background: var(--card);
  color: var(--text);
  border: 1px solid rgba(73, 182, 255, 0.12);
  padding: 10px 12px;
  border-radius: 12px;
  font-family: inherit;
}
.card input[type="file"] {
  padding: 10px;
}
.card button {
  background: linear-gradient(135deg, var(--accent-strong), var(--accent));
  color: #041327;
  border: none;
  padding: 10px 14px;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
}
.card button:disabled {
  opacity: 0.7;
  cursor: default;
}
.ghost {
  background: transparent;
  color: var(--muted);
  border: 1px solid rgba(73, 182, 255, 0.25);
  padding: 8px 12px;
  border-radius: 12px;
  cursor: pointer;
}
.state {
  color: var(--muted);
  padding: 10px 16px;
}
.state.success {
  color: var(--accent);
}
</style>
